import axios from 'axios'
import { CRM_STAGES } from '@/enums/crmOpportunity'
import store from '../index'
import { make } from 'vuex-pathify'
import { getQueryFromFilter, showError, showSuccess, allow } from '@/util/helpers'
import { PERMISSIONS } from '@/enums/permissions'

const writableFields = [
  'title',
  'responsibleId',
  'customerId',
  'customerContactId',
  'prospectName',
  'prospectCpfCnpj',
  'prospectRegistry',
  'prospectCategoryId',
  'phone',
  'email',
  'origin',
  'estimatedValue',
  'expectedCloseDate',
  'notes',
  'nextAction',
  'stage',
  'lossReason',
  'changeReason',
  'version',
]
const writableOpportunity = (item) => Object.fromEntries(writableFields.filter((key) => item[key] !== undefined).map((key) => [key, item[key]]))

const emptyFilter = () => ({ page: 1, itemsPerPage: 25, search: '', responsibleId: null, stage: null, origin: '', month: new Date().getMonth() + 1, year: new Date().getFullYear() })
const emptyList = () => ({ data: [], count: 0, totals: [] })
const state = {
  opportunities: emptyList(),
  filter: emptyFilter(),
  opportunity: {},
  dialog: false,
  loading: false,
  saving: false,
  moving: false,
  error: '',
  epoch: 0,
  sequence: 0,
  formalizing: null,
  ownsCustomerDialog: false,
  ownsRequestDialog: false,
}
const endpoint = (id) => `${process.env.VUE_APP_API_URL}/crm/opportunities${id ? `/${id}` : ''}?${getQueryFromFilter({})}`
const permitted = (permission) => allow(permission, store.get('authStore@userPermissions') || [])
const localDatetime = (value) => {
  const date = new Date(value)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}
const editable = (item) => ({ ...item, expectedCloseDate: item.expectedCloseDate ? String(item.expectedCloseDate).slice(0, 10) : '', nextActionAt: item.nextActionAt ? localDatetime(item.nextActionAt) : '', changeReason: '' })
const actions = {
  reset: ({ state }) => {
    if (state.ownsCustomerDialog) {
      store.set('customerStore/dialog', false)
      store.set('customerStore/customer', { addresses: [], contacts: [], equipments: [] })
    }
    if (state.ownsRequestDialog) {
      store.set('requestStore/dialog', false)
      store.set('requestStore/request', { items: [], payments: [] })
    }
    state.ownsCustomerDialog = false
    state.ownsRequestDialog = false
    state.epoch++
    state.sequence++
    state.opportunities = emptyList()
    state.filter = emptyFilter()
    state.opportunity = {}
    state.dialog = false
    state.loading = false
    state.saving = false
    state.moving = false
    state.error = ''
    state.formalizing = null
  },
  get: async ({ state }, filter = state.filter) => {
    const epoch = state.epoch
    const sequence = ++state.sequence
    state.loading = true
    state.error = ''
    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/crm/opportunities?${getQueryFromFilter(filter)}`)
      if (epoch === state.epoch && sequence === state.sequence) {
        state.opportunities = response.data
      }
      return response.data
    } catch (error) {
      if (epoch === state.epoch && sequence === state.sequence) {
        state.opportunities = emptyList()
        state.error = 'Não foi possível carregar as oportunidades. Tente novamente.'
        showError(error)
      }
    } finally {
      if (epoch === state.epoch && sequence === state.sequence) {
        state.loading = false
      }
    }
  },
  add: async ({ state }, data = {}) => {
    if (!permitted(PERMISSIONS.CRM_OPPORTUNITY_ADD)) {
      return
    }
    state.opportunity = { title: '', prospectName: '', responsibleId: store.get('authStore@currentUser').id, customerId: null, customerContactId: null, estimatedValue: 0, stage: 'new', ...data }
    state.dialog = true
    if (data.customerId) {
      const epoch = state.epoch
      const item = state.opportunity
      try {
        const res = await store.dispatch('customerStore/getById', data.customerId)
        if (epoch === state.epoch && state.opportunity === item) {
          state.opportunity = { ...item, responsibleId: res.data.userId || item.responsibleId }
        }
      } catch (error) {
        showError(error)
      }
    }
  },
  edit: async ({ state }, id) => {
    const epoch = state.epoch
    try {
      const response = await axios.get(endpoint(id))
      if (epoch !== state.epoch) {
        return
      }
      state.opportunity = editable(response.data)
      state.dialog = true
    } catch (error) {
      if (epoch === state.epoch) {
        showError(error)
      }
    }
  },
  save: async ({ state, dispatch }, item = state.opportunity) => {
    const epoch = state.epoch
    state.saving = true
    try {
      const payload = writableOpportunity(item)
      payload.expectedCloseDate = item.expectedCloseDate ? String(item.expectedCloseDate).slice(0, 10) : null
      payload.nextActionAt = item.nextActionAt ? new Date(item.nextActionAt).toISOString() : null
      const response = await axios[item.id ? 'put' : 'post'](endpoint(item.id), payload)
      if (epoch !== state.epoch) {
        return
      }
      state.opportunity = editable(response.data)
      showSuccess('Oportunidade salva com sucesso!')
      await dispatch('get')
      if (epoch !== state.epoch) {
        return
      }
      return response.data
    } catch (error) {
      if (epoch === state.epoch) {
        showError(error)
      }
    } finally {
      if (epoch === state.epoch) {
        state.saving = false
      }
    }
  },
  moveStage: async ({ state, dispatch }, { item, stage, lossReason, changeReason }) => {
    if (!permitted(PERMISSIONS.CRM_OPPORTUNITY_EDIT) || state.moving || state.saving || !item?.id || item.stage === stage || !CRM_STAGES.some((row) => row.value === stage)) {
      return
    }
    const epoch = state.epoch
    state.moving = true
    try {
      // The board contains summaries; saving one directly would erase omitted fields.
      const response = await axios.get(endpoint(item.id))
      if (epoch !== state.epoch || !permitted(PERMISSIONS.CRM_OPPORTUNITY_EDIT)) {
        return
      }
      if (Number(response.data.version) !== Number(item.version)) {
        showError('Oportunidade alterada por outro usuário. O quadro será atualizado.')
        await dispatch('get')
        return
      }
      return await dispatch('save', { ...response.data, stage, lossReason, changeReason })
    } catch (error) {
      if (epoch === state.epoch) {
        showError(error)
      }
    } finally {
      if (epoch === state.epoch) {
        state.moving = false
      }
    }
  },
  archive: async ({ state, dispatch }, item) => {
    const epoch = state.epoch
    try {
      await axios.delete(endpoint(item.id), { data: { version: item.version } })
      if (epoch !== state.epoch) {
        return
      }
      state.dialog = false
      showSuccess('Oportunidade arquivada com sucesso!')
      await dispatch('get')
    } catch (error) {
      if (epoch === state.epoch) {
        showError(error)
      }
    }
  },
  convert: async ({ state, dispatch }, item) => {
    if (!permitted(PERMISSIONS.CRM_OPPORTUNITY_EDIT) || !permitted(PERMISSIONS.REQUEST_ADD)) {
      return
    }
    if (item.requestId) {
      if (permitted(PERMISSIONS.REQUEST_VIEW)) {
        await dispatch('openRequest', item)
      }
      return
    }
    if (Number(item.companyId || store.get('authStore@currentCompany')?.id) !== Number(store.get('authStore@currentCompany')?.id)) {
      return showError('Selecione a empresa da oportunidade antes de gerar o pedido.')
    }
    if (!item.id) {
      return showError('Salve a oportunidade antes de gerar o pedido.')
    }
    if (item.stage === 'lost') {
      return showError('Oportunidade finalizada como Perdido não pode gerar pedido.')
    }
    if (!item.customerId) {
      return showError('Formalize e vincule um cliente antes de gerar o pedido.')
    }
    state.dialog = false
    state.ownsRequestDialog = true
    const draft = { ...writableOpportunity(item), expectedCloseDate: item.expectedCloseDate ? String(item.expectedCloseDate).slice(0, 10) : null, nextActionAt: item.nextActionAt ? new Date(item.nextActionAt).toISOString() : null }
    await store.dispatch('requestStore/add', { customerId: item.customerId, customerContactId: item.customerContactId, crmOpportunityId: item.id, crmOpportunityDraft: draft })
  },
  openRequest: async ({ state }, item) => {
    if (!permitted(PERMISSIONS.REQUEST_VIEW) || !item.requestId) {
      return
    }
    const epoch = state.epoch
    state.dialog = false
    state.ownsRequestDialog = true
    await store.dispatch('requestStore/edit', { id: item.requestId, type: 'view', crmEpoch: epoch })
  },
  formalize: async ({ state }, item) => {
    if (!permitted(PERMISSIONS.CUSTOMER_ADD) || !permitted(PERMISSIONS.CRM_OPPORTUNITY_EDIT) || item.customerId) {
      return
    }
    if (Number(item.companyId || store.get('authStore@currentCompany')?.id) !== Number(store.get('authStore@currentCompany')?.id)) {
      return showError('Selecione a empresa da oportunidade antes de formalizar o cliente.')
    }
    if (!item.id && !permitted(PERMISSIONS.CRM_OPPORTUNITY_ADD)) {
      return
    }
    const epoch = state.epoch
    state.formalizing = { id: item.id, epoch, item: { ...item } }
    state.ownsCustomerDialog = true
    await store.dispatch('customerStore/add')
    if (epoch !== state.epoch) {
      return
    }
    const customer = store.get('customerStore@customer')
    store.set('customerStore/customer', {
      ...customer,
      name: item.prospectName || '',
      phone: item.phone || '',
      email: item.email || '',
      cpfCnpj: item.prospectCpfCnpj || '',
      registry: item.prospectRegistry || '',
      categoryId: item.prospectCategoryId || null,
    })
  },
  customerCreated: async ({ state, dispatch }, { id, pending, customer = {} }) => {
    if (!pending || pending !== state.formalizing || pending.epoch !== state.epoch || !id) {
      return
    }
    state.formalizing = null
    try {
      const identity = [customer.name || pending.item.prospectName, customer.cpfCnpj || pending.item.prospectCpfCnpj].filter(Boolean).join(' - ')
      const saved = await dispatch('save', { ...pending.item, customerId: id, customerContactId: null, changeReason: `Cliente [${identity}] formalizado` })
      if (saved && pending.epoch === state.epoch) {
        await dispatch('edit', saved.id)
      }
    } catch (error) {
      if (pending.epoch === state.epoch) {
        showError(error)
      }
    }
  },
}
export default { namespaced: true, state, mutations: make.mutations(state), getters: make.getters(state), actions }
